import React, { useState, useRef } from "react";
import {
  Box,
  Avatar,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { CameraAlt as CameraIcon } from "@mui/icons-material";

const ProfileImageUpload = ({ 
  currentImage, 
  onImageChange, 
  loading = false,
  size = 120 
}) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      alert("Please select a valid image file (JPEG, PNG, GIF, WebP)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size should be less than 5MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      onImageChange(file);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setPreview(null);
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const imageUrl = preview || currentImage;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mb: 3,
        position: "relative",
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Avatar
          src={imageUrl}
          sx={{
            width: size,
            height: size,
            border: "4px solid white",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            bgcolor: "#f0f4f8",
          }}
        >
          {!imageUrl && (
            <Typography variant="h4" sx={{ color: "#46639c", fontWeight: 700 }}>
              {currentImage ? "" : "U"}
            </Typography>
          )}
        </Avatar>

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        <IconButton
          onClick={() => fileInputRef.current?.click()}
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            backgroundColor: "#f97316",
            color: "white",
            "&:hover": {
              backgroundColor: "#f5812e",
            },
            width: 40,
            height: 40,
          }}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <CameraIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Typography
        variant="body2"
        sx={{
          mt: 2,
          color: "#46639c",
          cursor: "pointer",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        Upload Photo
      </Typography>

      {imageUrl && (
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: "#f44336",
            cursor: "pointer",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={handleRemoveImage}
        >
          Remove
        </Typography>
      )}

      <Typography
        variant="caption"
        sx={{
          mt: 1,
          color: "text.secondary",
          textAlign: "center",
          maxWidth: 200,
        }}
      >
        Recommended: Square image, 500x500px, max 5MB
      </Typography>
    </Box>
  );
};

export default ProfileImageUpload;